import type React from "react";
import { memo } from "react";
import { useMobileBreakpoint } from "../hooks/useMobileBreakpoint";

interface MainLayoutProps {
	activeTab: "transcript" | "comments";
	leftPanel: React.ReactNode;
	rightPanel: React.ReactNode;
}

/**
 * Main application layout, memoized to prevent re-renders of the entire shell
 * when one panel or unrelated state updates. Requires stable panel props
 * (via useMemo in the parent) to be effective.
 */
export const MainLayout: React.FC<MainLayoutProps> = memo(
	({ activeTab, leftPanel, rightPanel }) => {
		const isMobile = useMobileBreakpoint(600);

		return (
			<main
				className={`flex-1 flex overflow-hidden p-4 gap-6 bg-bg-main
				max-[900px]:flex-col max-[900px]:overflow-y-auto max-[900px]:p-3 max-[900px]:gap-4
				max-[600px]:h-[calc(100vh-var(--spacing-header)-var(--spacing-controls)-48px)]
				max-[600px]:h-[calc(100dvh-var(--spacing-header)-var(--spacing-controls)-48px)]
				max-[600px]:overflow-hidden
				${activeTab === "comments" ? "show-comments" : ""}`}
			>
				<div
					id="mobile-panel-transcript"
					{...(isMobile
						? {
								role: "tabpanel" as const,
								"aria-labelledby": "mobile-tab-transcript",
							}
						: {})}
					className={`flex-2 flex flex-col gap-4 min-w-0 min-h-0
					max-[900px]:flex-none max-[900px]:w-full max-[900px]:flex
					max-[600px]:flex-1 max-[600px]:flex-col max-[600px]:min-h-0
					${activeTab === "comments" ? "max-[600px]:hidden" : "max-[600px]:flex"}`}
				>
					{leftPanel}
				</div>
				<div
					id="mobile-panel-comments"
					{...(isMobile
						? {
								role: "tabpanel" as const,
								"aria-labelledby": "mobile-tab-comments",
							}
						: {})}
					className={`flex-1 flex flex-col bg-card-bg border border-border-main rounded-xl overflow-hidden min-w-0 min-h-0
					max-[900px]:flex-none max-[900px]:w-full max-[900px]:flex
					max-[600px]:flex-1 max-[600px]:flex-col max-[600px]:min-h-0
					${activeTab === "comments" ? "max-[600px]:flex" : "max-[600px]:hidden"}`}
				>
					{rightPanel}
				</div>
			</main>
		);
	},
);
